import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/utils/supabase';
import { Container, Logo } from '@beyond-silence/ui';

/**
 * Staff login page.
 * Uses Supabase Auth with email/password.
 * Redirects to /dashboard on success.
 */
export function LoginPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);

    if (authError) {
      setError(authError.message);
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-4">
      <Container size="article" className="max-w-sm">
        <div className="rounded-lg border border-soft/60 bg-white p-6 shadow-sm sm:p-8">
          {/* Logo */}
          <div className="mb-6 text-center">
            <Logo size="lg" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="block font-ui text-ui-sm font-medium text-ink">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-ui text-ui-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="staff@beyondsilence.fr"
              />
            </div>

            <div>
              <label htmlFor="password" className="block font-ui text-ui-sm font-medium text-ink">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="mt-1 w-full rounded-md border border-soft/60 bg-bg px-3 py-2 font-ui text-ui-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            {error && (
              <p className="rounded-md bg-primary/10 px-3 py-2 font-ui text-ui-sm text-primary">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-pill bg-primary px-4 py-2.5 font-ui text-ui-base font-medium text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {loading ? t('states.loading') : 'Sign in'}
            </button>
          </form>
        </div>
      </Container>
    </div>
  );
}
